import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheck, HiExclamationCircle, HiX } from 'react-icons/hi';
import { Modal, Button } from '@/components/common';
import { DebtAdjustmentVoucherResponse } from '@/types/debtAdjustmentVoucher';
import { debtAdjustmentVoucherApi } from '@/api/endpoints/debtAdjustmentVouchers';
import { toast } from '@/store/toastStore';
import { formatCurrency } from '@/utils/number-format';

interface ConfirmDebtAdjustmentVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  voucher: DebtAdjustmentVoucherResponse | null;
  action: 'confirm' | 'cancel';
  currentDebt?: number; // Current debt balance for warning
}

const ConfirmDebtAdjustmentVoucherModal: React.FC<ConfirmDebtAdjustmentVoucherModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  voucher,
  action,
  currentDebt,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const getActionConfig = () => {
    switch (action) {
      case 'confirm':
        return {
          title: t('debtAdjustmentVoucher.buttons.confirm', 'Xác nhận phiếu điều chỉnh công nợ'),
          message: `Bạn có chắc chắn muốn xác nhận phiếu điều chỉnh công nợ ${voucher?.code}?`,
          icon: <HiCheck className="w-6 h-6 text-green-600" />,
          confirmText: t('debtAdjustmentVoucher.buttons.confirm', 'Xác nhận'),
          confirmClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
          successMessage: t('debtAdjustmentVoucher.messages.confirmSuccess', 'Xác nhận phiếu điều chỉnh công nợ thành công'),
        };
      case 'cancel':
        return {
          title: t('debtAdjustmentVoucher.buttons.cancel', 'Hủy phiếu điều chỉnh công nợ'),
          message: `Bạn có chắc chắn muốn hủy phiếu điều chỉnh công nợ ${voucher?.code}?`,
          icon: <HiX className="w-6 h-6 text-red-600" />,
          confirmText: t('common.agree', 'Đồng ý'),
          confirmClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          successMessage: t('debtAdjustmentVoucher.messages.cancelSuccess', 'Hủy phiếu điều chỉnh công nợ thành công'),
        };
      default:
        return {
          title: '',
          message: '',
          icon: null,
          confirmText: '',
          confirmClass: '',
          successMessage: '',
        };
    }
  };

  const handleConfirm = async () => {
    if (!voucher) return;

    setLoading(true);
    try {
      switch (action) {
        case 'confirm':
          await debtAdjustmentVoucherApi.confirmDebtAdjustmentVoucher(voucher.code);
          break;
        case 'cancel':
          await debtAdjustmentVoucherApi.cancelDebtAdjustmentVoucher(voucher.code);
          break;
      }

      const config = getActionConfig();
      toast.success(config.successMessage);
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error(`Error ${action} debt adjustment voucher:`, error);
      const errorMessage = error?.response?.data?.message;
      toast.error(
        errorMessage ||
          t(`debtAdjustmentVoucher.messages.${action}Error`, `Thao tác với phiếu điều chỉnh công nợ thất bại`),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!voucher) return null;

  const config = getActionConfig();
  
  // Calculate new debt after adjustment
  const newDebt = currentDebt !== undefined 
    ? currentDebt + voucher.adjustment_amount 
    : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={config.title} size="md">
      <div className="p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">{config.icon}</div>
          <div className="flex-1">
            <p className="text-sm text-gray-700 mb-4">{config.message}</p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Mã phiếu: </span>
                  <span className="text-gray-900">{voucher.code}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    {voucher.entity_type === 'customer' 
                      ? t('debtAdjustmentVoucher.fields.customer')
                      : t('debtAdjustmentVoucher.fields.supplier')}: 
                  </span>
                  <span className="ml-2 text-gray-900">
                    {voucher.customer?.name || voucher.supplier?.name || voucher.entity_id}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    {t('debtAdjustmentVoucher.fields.amount')}: 
                  </span>
                  <span className={`ml-2 font-medium ${
                    voucher.adjustment_amount < 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(voucher.adjustment_amount)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    {t('debtAdjustmentVoucher.fields.reason')}: 
                  </span>
                  <span className="ml-2 text-gray-900">{voucher.reason}</span>
                </div>
                {currentDebt !== undefined && newDebt !== null && (
                  <div>
                    <span className="font-medium text-gray-700">Công nợ sau điều chỉnh: </span>
                    <span className={`ml-2 font-medium ${newDebt < 0 ? 'text-red-600' : ''}`}>
                      {formatCurrency(newDebt)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {action === 'confirm' && (
              <>
                {newDebt !== null && newDebt < 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                    <div className="flex">
                      <HiExclamationCircle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-700">
                          {t('debtAdjustmentVoucher.validation.debtCannotBeNegative', 'Công nợ không được âm sau khi điều chỉnh')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <div className="flex">
                    <HiExclamationCircle className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Sau khi xác nhận, không thể chỉnh sửa phiếu điều chỉnh công nợ.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            loading={loading}
            disabled={newDebt !== null && newDebt < 0 && action === 'confirm'}
            className={`text-white ${config.confirmClass}`}
          >
            {config.confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDebtAdjustmentVoucherModal;

